import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PlatformLayout from "@/layouts/PlatformLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Plus, ChevronRight, MapPin, Gavel, Loader2, FolderOpen } from "lucide-react";
import { API } from "@/constants";

const PlatformCasesPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/cases`, { withCredentials: true });
        setCases(res.data.cases || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchCases();
  }, []);

  return (
    <PlatformLayout activeTab="cases">
      <div className="p-6 max-w-6xl mx-auto" data-testid="platform-cases">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Case Management</h1>
            <p className="text-sm text-slate-500">{cases.length} cases in your portfolio</p>
          </div>
          <Link to="/tools/litix-wizard">
            <Button className="bg-blue-600 hover:bg-blue-500">
              <Plus className="h-4 w-4 mr-1.5" /> New Case
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : cases.length === 0 ? (
          <Card>
            <CardContent className="text-center py-16">
              <FolderOpen className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <h3 className="font-semibold text-lg mb-1">No Cases Yet</h3>
              <p className="text-sm text-slate-500 mb-4">Start your first case with the Litix Wizard</p>
              <Link to="/tools/litix-wizard">
                <Button><Plus className="h-4 w-4 mr-1.5" /> Create First Case</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {cases.map((c, i) => (
              <Link key={i} to={`/case/${c.case_id}`} data-testid={`case-row-${i}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer border-0 shadow-sm mb-2">
                  <CardContent className="py-4 px-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{c.case_name || "Untitled Case"}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {c.case_number && <span className="text-xs text-slate-400">#{c.case_number}</span>}
                          {c.state && (
                            <span className="text-xs text-slate-400 flex items-center gap-0.5">
                              <MapPin className="h-2.5 w-2.5" />{c.state}
                            </span>
                          )}
                          {c.court_type && (
                            <span className="text-xs text-slate-400 flex items-center gap-0.5">
                              <Gavel className="h-2.5 w-2.5" />{c.court_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {(c.torts || []).slice(0, 2).map((t, j) => (
                        <Badge key={j} variant="outline" className="text-[10px]">{t}</Badge>
                      ))}
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
};

export default PlatformCasesPage;
